import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as installationActions from '../../actions/installation';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaFileManager, { MikaFile, UploadSlot } from '../../components/MikaFileManager/MikaFileManager';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import { INSTALLATION_STATUS_COLORS } from '../../constants/constants';
import { toast } from 'react-toastify';
import '../Contact/Contact.scss';
import '../Transport/Transport.scss';
import MentionInput from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionInput/MentionText';

const fmtDate = (d: string | null): string => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
};

const fmtDateOnly = (d: string | null): string => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
};

const fmtDtShort = (d: string | null): string => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
};

const eventColors: Record<string, string> = {
    created: '#3b82f6', blocked: '#ef4444', resumed: '#3b82f6',
    left: '#f97316', completed: '#22c55e', file_uploaded: '#64748b',
    status_changed: '#f59e0b',
};

interface Props {
    currentInstallation: any;
    permissions: any;
    loading: boolean;
    actions: any;
    history: any;
    match: any;
}

const InstallationDetail: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    const formatEvent = (entry: any): string => {
        const t = entry?.eventType;
        const meta = entry?.metadata || {};
        if (t === 'created') return fm('installation.detail.event.created', 'Instalimi u krijua');
        if (t === 'blocked') {
            if (meta.category_name && meta.type_name) return fm('installation.detail.event.blockedWithCategoryType', 'Instalimi u bllokua: {category} > {type}', { category: meta.category_name, type: meta.type_name });
            if (meta.category_name) return fm('installation.detail.event.blockedWithCategory', 'Instalimi u bllokua: {category}', { category: meta.category_name });
            if (meta.reason) return fm('installation.detail.event.blockedWithReason', 'Instalimi u bllokua: {reason}', { reason: meta.reason });
            return fm('installation.detail.event.blocked', 'Instalimi u bllokua');
        }
        if (t === 'resumed') return fm('installation.detail.event.resumed', 'Puna u vazhdua');
        if (t === 'left') return fm('installation.detail.event.left', 'Ekipi u largua');
        if (t === 'completed') return fm('installation.detail.event.completed', 'Instalimi u përfundua');
        if (t === 'file_uploaded') return fm('installation.detail.event.fileUploaded', 'Dokumenti u ngarkua');
        if (t === 'status_changed') return fm('installation.detail.event.statusChanged', 'Statusi u ndryshua');
        return entry?.description || '';
    };

    const formatActiveSeconds = (seconds: number): string => {
        if (seconds <= 0) return fm('installation.detail.duration.zeroMin', '0 min');
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const parts: string[] = [];
        if (days > 0) parts.push(fm('installation.detail.duration.days', '{count} ditë', { count: days }));
        if (hours > 0) parts.push(fm('installation.detail.duration.hours', '{count} orë', { count: hours }));
        if (minutes > 0 || parts.length === 0) parts.push(fm('installation.detail.duration.minutes', '{count} min', { count: minutes }));
        return parts.join(', ');
    };

    const installationId = parseInt(props.match.params.installationId);
    const [newCommentText, setNewCommentText] = useState('');
    const [blockComment, setBlockComment] = useState('');
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [blockCategories, setBlockCategories] = useState<any[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
    const [liveActiveSeconds, setLiveActiveSeconds] = useState(0);
    const [deleteConfirm, setDeleteConfirm] = useState<{ fileIds: number[]; message: string } | null>(null);
    const [showBlockHistory, setShowBlockHistory] = useState(false);
    const [showVisits, setShowVisits] = useState(false);
    const [confirmAction, setConfirmAction] = useState<{ type: 'leave' | 'finish'; title: string; message: string } | null>(null);

    const inst = props.currentInstallation;

    // Group items by project for delivery cards
    const deliveryGroups = useMemo(() => {
        if (!inst?.transportItem) return [];
        const wr = inst.transportItem?.warehouseRecord;
        if (!wr) return [];
        return [{
            client: inst.client,
            project: inst.project,
            measureLocation: inst.measureLocation,
            warehouseRecord: wr,
        }];
    }, [inst]);

    useEffect(() => { if (installationId) props.actions.fetchInstallation(installationId); }, [installationId]);

    // Fetch block categories
    useEffect(() => {
        import('../../utils/apiCaller').then(function(mod) {
            mod.axiosCaller('GET', 'installation-block-categories').then(function(res: any) {
                setBlockCategories(res?.data || []);
            });
        });
    }, []);

    // Live timer: sync from API value, then tick every 60s when in progress
    useEffect(() => {
        if (inst?.activeSeconds !== undefined) {
            setLiveActiveSeconds(inst.activeSeconds);
        }
    }, [inst?.activeSeconds]);

    useEffect(() => {
        if (inst?.status !== 1) return;
        const interval = setInterval(() => {
            setLiveActiveSeconds(prev => prev + 60);
        }, 60000);
        return () => clearInterval(interval);
    }, [inst?.status]);

    if (!inst) return <div className="clp-page" style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>{fm('installation.detail.loading', 'Duke u ngarkuar...')}</div>;

    const statusColors = INSTALLATION_STATUS_COLORS[inst.status] || { color: '#94a3b8', bg: '#f1f5f9' };

    const selectedCategory = blockCategories.find(function(c: any) { return c.id === selectedCategoryId; });
    const categoryTypes = selectedCategory?.types || [];
    const isTjeter = selectedCategory?.is_protected === true;

    const handleBlock = async () => {
        if (!selectedCategoryId) { toast.error(fm('installation.detail.toast.selectCategory', 'Zgjidh kategorinë')); return; }
        if (isTjeter && !blockComment.trim()) { toast.error(fm('installation.detail.toast.commentRequiredOther', 'Shkruaj komentin për kategorinë "Tjetër"')); return; }
        if (categoryTypes.length > 0 && !selectedTypeId && !blockComment.trim()) { toast.error(fm('installation.detail.toast.typeOrCommentRequired', 'Zgjidh llojin ose shkruaj një koment')); return; }

        const result = await props.actions.blockInstallation(installationId, {
            block_category_id: selectedCategoryId,
            block_type_id: selectedTypeId || undefined,
            block_reason: blockComment.trim() || undefined,
        });
        if (result?.error) { toast.error(result.message || fm('installation.detail.toast.genericError', 'Gabim')); return; }
        toast.success(fm('installation.detail.toast.blocked', 'Instalimi u bllokua'));
        setShowBlockModal(false);
        setSelectedCategoryId(null); setSelectedTypeId(null); setBlockComment('');
        props.actions.fetchInstallation(installationId);
    };

    const handleResume = async () => {
        const result = await props.actions.resumeInstallation(installationId);
        if (result?.error) { toast.error(result.message || fm('installation.detail.toast.genericError', 'Gabim')); return; }
        toast.success(fm('installation.detail.toast.resumed', 'Puna u vazhdua'));
        props.actions.fetchInstallation(installationId);
    };

    const handleLeave = async () => {
        setConfirmAction({
            type: 'leave',
            title: fm('installation.detail.confirm.leaveTitle', 'Ndërprit instalimin'),
            message: fm('installation.detail.confirm.leaveMessage', 'Jeni të sigurt që doni të largoni ekipin? Instalimi do të shënohet si pjesërisht.'),
        });
    };

    const handleFinish = async () => {
        setConfirmAction({
            type: 'finish',
            title: fm('installation.detail.confirm.finishTitle', 'Përfundo instalimin'),
            message: fm('installation.detail.confirm.finishMessage', 'Jeni të sigurt që doni të përfundoni instalimin?'),
        });
    };

    const executeConfirmAction = async () => {
        if (!confirmAction) return;
        if (confirmAction.type === 'leave') {
            const result = await props.actions.leaveInstallation(installationId);
            if (result?.error) { toast.error(result.message || fm('installation.detail.toast.genericError', 'Gabim')); setConfirmAction(null); return; }
            toast.success(fm('installation.detail.toast.left', 'Ekipi u largua'));
        } else if (confirmAction.type === 'finish') {
            const result = await props.actions.finishInstallation(installationId);
            if (result?.error) { toast.error(result.message || fm('installation.detail.toast.genericError', 'Gabim')); setConfirmAction(null); return; }
            toast.success(fm('installation.detail.toast.finished', 'Instalimi u përfundua'));
        }
        setConfirmAction(null);
        props.actions.fetchInstallation(installationId);
    };

    const handleAddComment = async () => {
        if (!newCommentText.trim()) return;
        await props.actions.addInstallationComment(installationId, newCommentText.trim());
        setNewCommentText('');
        props.actions.fetchInstallation(installationId);
    };

    const parseCoords = (loc: string | null) => {
        if (!loc) return null;
        const parts = loc.split(',').map(s => parseFloat(s.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return parts;
        return null;
    };

    return (
        <div className="clp-page" style={{ gap: 12 }}>
            <MikaPageHeader
                title={fm('installation.detail.pageTitle', 'Instalimi #{id}', { id: inst.id })}
                actions={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: statusColors.color, background: statusColors.bg }}>
                            {(() => {
                                if (inst.status === 1) return fm('installation.strip.inProgress', 'Në proces');
                                if (inst.status === 2) return fm('installation.strip.blocked', 'E bllokuar');
                                if (inst.status === 3) return fm('installation.strip.partial', 'Pjesërisht');
                                if (inst.status === 4) return fm('installation.strip.completed', 'E përfunduar');
                                return inst.statusLabel;
                            })()}
                        </span>
                        {inst.isOutsourced && <span style={{ padding: '3px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, color: '#f97316', background: '#fff7ed' }}>{fm('installation.installer.outsourced', 'E JASHTME')}</span>}
                        {inst.status === 1 && (
                            <>
                                <button className="inst-action-btn inst-action-finish" onClick={handleFinish}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                    <FormattedMessage id="installation.detail.action.finish" defaultMessage="Përfundo" />
                                </button>
                                <button className="inst-action-btn inst-action-header-secondary" onClick={handleLeave}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                    <FormattedMessage id="installation.detail.action.leave" defaultMessage="Ndërprit" />
                                </button>
                                <button className="inst-action-btn inst-action-header-danger" onClick={function() { setShowBlockModal(true); }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                                    <FormattedMessage id="installation.detail.action.block" defaultMessage="Blloko" />
                                </button>
                            </>
                        )}
                        {(inst.status === 2 || inst.status === 3) && (
                            <button className="inst-action-btn inst-action-resume" onClick={handleResume}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                <FormattedMessage id="installation.detail.action.resume" defaultMessage="Vazhdo" />
                            </button>
                        )}
                    </div>
                }
            />

            {/* Block modal */}
            {showBlockModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={function() { setShowBlockModal(false); }}>
                    <div style={{ background: '#fff', borderRadius: 14, padding: 0, width: 440, maxWidth: '100%', boxShadow: '0 24px 64px rgba(15,23,42,0.2)', animation: 'mikaScaleIn 0.2s ease', overflow: 'hidden' }}
                        onClick={function(e) { e.stopPropagation(); }}>
                        <div style={{ padding: '18px 22px 0' }}>
                            <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#0f172a' }}><FormattedMessage id="installation.detail.block.title" defaultMessage="Blloko instalimin" /></h3>
                            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}><FormattedMessage id="installation.detail.block.subtitle" defaultMessage="Zgjidh arsyen e bllokimit" /></p>
                        </div>
                        <div style={{ padding: '0 22px 22px', display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '60vh', overflow: 'auto' }}>
                            {/* Category */}
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    <FormattedMessage id="installation.detail.block.categoryLabel" defaultMessage="Kategoria" /> <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <select
                                    value={selectedCategoryId || ''}
                                    onChange={function(e) {
                                        var val = e.target.value ? parseInt(e.target.value) : null;
                                        setSelectedCategoryId(val);
                                        setSelectedTypeId(null);
                                    }}
                                    style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 13, outline: 'none', background: '#fff', color: '#0f172a' }}>
                                    <option value="">{fm('installation.detail.block.categoryPlaceholder', 'Zgjidh kategorinë...')}</option>
                                    {blockCategories.map(function(cat: any) {
                                        return <option key={cat.id} value={cat.id}>{cat.name}</option>;
                                    })}
                                </select>
                            </div>

                            {/* Type (only if category has types) */}
                            {categoryTypes.length > 0 && (
                                <div>
                                    <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                        <FormattedMessage id="installation.detail.block.typeLabel" defaultMessage="Lloji" /> {!blockComment.trim() && <span style={{ color: '#ef4444' }}>*</span>}
                                    </label>
                                    <select
                                        value={selectedTypeId || ''}
                                        onChange={function(e) { setSelectedTypeId(e.target.value ? parseInt(e.target.value) : null); }}
                                        style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 13, outline: 'none', background: '#fff', color: '#0f172a' }}>
                                        <option value="">{fm('installation.detail.block.typePlaceholder', 'Zgjidh llojin...')}</option>
                                        {categoryTypes.map(function(t: any) {
                                            return <option key={t.id} value={t.id}>{t.name}</option>;
                                        })}
                                    </select>
                                </div>
                            )}

                            {/* Comment */}
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    <FormattedMessage id="installation.detail.block.commentLabel" defaultMessage="Komenti" /> {(isTjeter || (categoryTypes.length > 0 && !selectedTypeId)) && <span style={{ color: '#ef4444' }}>*</span>}
                                </label>
                                <textarea
                                    value={blockComment}
                                    onChange={function(e) { setBlockComment(e.target.value); }}
                                    placeholder={isTjeter ? fm('installation.detail.block.reasonPlaceholder', 'Shkruaj arsyen...') : fm('installation.detail.block.commentPlaceholder', 'Shënim shtesë (opsionale nëse keni zgjedhur llojin)')}
                                    style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d0dbe8', fontSize: 13, minHeight: 70, resize: 'vertical', outline: 'none', boxSizing: 'border-box' as any }} />
                            </div>
                        </div>
                        <div style={{ padding: '14px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={function() { setShowBlockModal(false); }} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#64748b', fontWeight: 500 }}><FormattedMessage id="common.action.cancel" defaultMessage="Anulo" /></button>
                            <button onClick={handleBlock} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 8px rgba(239,68,68,0.3)' }}><FormattedMessage id="installation.detail.action.block" defaultMessage="Blloko" /></button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirmation modal */}
            <MikaConfirmModal
                isOpen={!!deleteConfirm}
                onClose={function() { setDeleteConfirm(null); }}
                onConfirm={async function() {
                    if (!deleteConfirm) return;
                    if (deleteConfirm.fileIds.length === 1) {
                        await props.actions.deleteInstallationFileNew(installationId, deleteConfirm.fileIds[0]);
                    } else {
                        await props.actions.deleteInstallationFilesNew(installationId, deleteConfirm.fileIds);
                    }
                    setDeleteConfirm(null);
                    props.actions.fetchInstallation(installationId);
                }}
                title={fm('installation.detail.file.deleteTitle', 'Fshi dokumentin')}
                message={deleteConfirm?.message || ''}
                confirmLabel={fm('installation.detail.file.delete', 'Fshi')}
                confirmColor="danger"
            />

            {/* Action confirm modal (leave/finish) */}
            <MikaConfirmModal
                isOpen={!!confirmAction}
                onClose={function() { setConfirmAction(null); }}
                onConfirm={executeConfirmAction}
                title={confirmAction?.title || ''}
                message={confirmAction?.message || ''}
                confirmLabel={confirmAction?.type === 'finish' ? fm('installation.detail.action.finish', 'Përfundo') : fm('installation.detail.action.leave', 'Ndërprit')}
                confirmColor={confirmAction?.type === 'finish' ? 'primary' : 'danger'}
            />

            {/* Block history popup */}
            {showBlockHistory && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                    onClick={function() { setShowBlockHistory(false); }}>
                    <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 24px 64px rgba(15,23,42,0.2)', animation: 'mikaScaleIn 0.2s ease' }}
                        onClick={function(e) { e.stopPropagation(); }}>
                        <div style={{ padding: '18px 22px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{fm('installation.detail.blockHistory.title', 'Historiku i bllokimeve ({count})', { count: inst.blockCount })}</h3>
                            <button onClick={function() { setShowBlockHistory(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                            </button>
                        </div>
                        <div style={{ padding: '0 22px 22px', maxHeight: '60vh', overflow: 'auto' }}>
                            {(inst.events || []).filter(function(e: any) { return e.eventType === 'blocked'; }).length === 0 && (
                                <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 13 }}>{fm('installation.detail.blockHistory.empty', 'Nuk ka bllokime të regjistruara')}</div>
                            )}
                            {(inst.events || []).filter(function(e: any) { return e.eventType === 'blocked'; }).map(function(entry: any) {
                                var meta = entry.metadata || {};
                                return (
                                    <div key={entry.id} style={{ padding: '12px 14px', background: '#fef2f2', borderRadius: 8, marginBottom: 8, borderLeft: '3px solid #ef4444' }}>
                                        {meta.category_name ? (
                                            <>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: '#991b1b', marginBottom: 2 }}>
                                                    {meta.category_name}{meta.type_name ? ' > ' + meta.type_name : ''}
                                                </div>
                                                {meta.reason && <div style={{ fontSize: 13, color: '#991b1b', lineHeight: 1.5 }}>{meta.reason}</div>}
                                            </>
                                        ) : (
                                            <div style={{ fontSize: 13, color: '#991b1b', lineHeight: 1.5 }}>{entry.description.replace('Instalimi u bllokua: ', '')}</div>
                                        )}
                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontWeight: 600, color: '#64748b' }}>{entry.user?.name || fm('installation.detail.unknownUser', 'I panjohur')}</span>
                                            <span>{fmtDate(entry.createdAt)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>

                {/* KPI Strip */}
                <div className="inst-kpi-strip">
                    <div className="inst-kpi-card">
                        <div className="inst-kpi-icon" style={{ background: '#eef3f9', color: '#1e3a5f' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        </div>
                        <div className="inst-kpi-info">
                            <div className="inst-kpi-value">{inst.isOutsourced ? (inst.outsourcedCompany || fm('installation.detail.kpi.outsourcedValue', 'E jashtme')) : (inst.installer?.name || '-')}</div>
                            <div className="inst-kpi-label">{inst.isOutsourced ? fm('installation.detail.kpi.outsourcedCompanyLabel', 'Kompania e jashtme') : fm('installation.col.installer', 'Instaluesi')}</div>
                        </div>
                    </div>
                    <div className="inst-kpi-card">
                        <div className="inst-kpi-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        </div>
                        <div className="inst-kpi-info">
                            <div className="inst-kpi-value">{formatActiveSeconds(liveActiveSeconds)}{inst.status === 1 && <span className="inst-kpi-live" />}</div>
                            <div className="inst-kpi-label">{fm('installation.col.activeDuration', 'Koha aktive')}</div>
                        </div>
                    </div>
                    {props.permissions?.fullPrivileges && (
                        <div className="inst-kpi-card">
                            <div className="inst-kpi-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                            </div>
                            <div className="inst-kpi-info">
                                <div className="inst-kpi-value">{inst.productValue > 0 ? `€${inst.productValue.toFixed(2)}` : '-'}</div>
                                <div className="inst-kpi-label">{fm('installation.col.value', 'Vlera')}</div>
                            </div>
                        </div>
                    )}
                    <div className="inst-kpi-card">
                        <div className="inst-kpi-icon" style={{ background: '#eef3f9', color: '#1e3a5f' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        </div>
                        <div className="inst-kpi-info">
                            <div className="inst-kpi-value">{fmtDate(inst.installationStart)}</div>
                            <div className="inst-kpi-label">{fm('installation.col.start', 'Fillimi')}</div>
                        </div>
                    </div>
                    {inst.plannedDate && (
                        <div className="inst-kpi-card" style={{ borderColor: inst.plannedDateStatus === 'overdue' ? '#fecaca' : inst.plannedDateStatus === 'today' ? '#fde68a' : '#bbf7d0' }}>
                            <div className="inst-kpi-icon" style={{ background: inst.plannedDateStatus === 'overdue' ? '#fef2f2' : inst.plannedDateStatus === 'today' ? '#fffbeb' : '#f0fdf4', color: inst.plannedDateStatus === 'overdue' ? '#ef4444' : inst.plannedDateStatus === 'today' ? '#f59e0b' : '#22c55e' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M12 6v6l4 2"/></svg>
                            </div>
                            <div className="inst-kpi-info">
                                <div className="inst-kpi-value" style={{ color: inst.plannedDateStatus === 'overdue' ? '#ef4444' : inst.plannedDateStatus === 'today' ? '#f59e0b' : '#22c55e' }}>{fmtDateOnly(inst.plannedDate)}</div>
                                <div className="inst-kpi-label">{fm('installation.col.plannedDate', 'Data e planifikuar')}</div>
                            </div>
                        </div>
                    )}
                    {inst.blockCount > 0 && (
                        <div className="inst-kpi-card" style={{ borderColor: '#fecaca', cursor: 'pointer' }} onClick={function() { setShowBlockHistory(true); }}>
                            <div className="inst-kpi-icon" style={{ background: '#fef2f2', color: '#ef4444' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                            </div>
                            <div className="inst-kpi-info">
                                <div className="inst-kpi-value" style={{ color: '#ef4444' }}>{inst.blockCount}</div>
                                <div className="inst-kpi-label">{fm('installation.col.blockCount', 'Bllokime')}</div>
                            </div>
                        </div>
                    )}
                    {inst.visits && inst.visits.length > 0 && (
                        <div className="inst-kpi-card" style={{ borderColor: '#bfdbfe', cursor: 'pointer' }} onClick={function() { setShowVisits(true); }}>
                            <div className="inst-kpi-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                            </div>
                            <div className="inst-kpi-info">
                                <div className="inst-kpi-value" style={{ color: '#3b82f6' }}>{inst.visits.length}</div>
                                <div className="inst-kpi-label">{fm('installation.detail.kpi.visits', 'Vizita')}</div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Block reason banner */}
                {inst.status === 2 && (inst.blockReason || inst.blockCategoryName) && (
                    <div className="inst-block-banner">
                        <div className="inst-block-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                        </div>
                        <div className="inst-block-text">
                            <div className="inst-block-label"><FormattedMessage id="installation.detail.blockBanner.label" defaultMessage="Arsyeja e bllokimit" /></div>
                            <div className="inst-block-reason">
                                {inst.blockCategoryName && <strong>{inst.blockCategoryName}</strong>}
                                {inst.blockTypeName && <span> &gt; {inst.blockTypeName}</span>}
                                {inst.blockReason && <div style={{ marginTop: 2 }}>{inst.blockReason}</div>}
                            </div>
                        </div>
                    </div>
                )}

                {/* Visits popup */}
                {showVisits && inst.visits && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                        onClick={function() { setShowVisits(false); }}>
                        <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 500, maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 24px 64px rgba(15,23,42,0.2)', animation: 'mikaScaleIn 0.2s ease' }}
                            onClick={function(e) { e.stopPropagation(); }}>
                            <div style={{ padding: '18px 22px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{fm('installation.detail.visits.title', 'Historiku i vizitave ({count})', { count: inst.visits.length })}</h3>
                                <button onClick={function() { setShowVisits(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                </button>
                            </div>
                            <div style={{ padding: '0 22px 22px', maxHeight: '60vh', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {inst.visits.map(function(v: any) {
                                    var reasonLabels: Record<string, string> = {
                                        blocked: fm('installation.detail.visits.reason.blocked', 'Bllokuar'),
                                        left: fm('installation.detail.visits.reason.left', 'Larguar'),
                                        finished: fm('installation.detail.visits.reason.finished', 'Përfunduar'),
                                    };
                                    var borderColors: Record<string, string> = { blocked: '#ef4444', left: '#f97316', finished: '#22c55e' };
                                    var bgColors: Record<string, string> = { blocked: '#fef2f2', left: '#fff7ed', finished: '#f0fdf4' };
                                    var dotColors: Record<string, string> = { blocked: '#ef4444', left: '#f97316', finished: '#22c55e' };
                                    var isActive = !v.endedAt;
                                    var borderColor = isActive ? '#3b82f6' : (borderColors[v.endedReason] || '#e2e8f0');
                                    var bgColor = isActive ? '#eff6ff' : (bgColors[v.endedReason] || '#f8fafc');

                                    return (
                                        <div key={v.id} style={{
                                            borderRadius: 8, border: '1px solid ' + (isActive ? '#bfdbfe' : '#e2e8f0'),
                                            borderLeft: '3px solid ' + borderColor,
                                            background: bgColor, padding: '10px 14px',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                                <span style={{
                                                    width: 24, height: 24, borderRadius: 6,
                                                    background: isActive ? '#dbeafe' : '#f1f5f9',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 11, fontWeight: 700,
                                                    color: isActive ? '#3b82f6' : '#64748b', flexShrink: 0,
                                                }}>
                                                    {v.visitNumber}
                                                </span>
                                                {isActive ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>
                                                        <span className="inst-kpi-live" style={{ marginLeft: 0 }}></span>
                                                        <FormattedMessage id="installation.detail.visits.inProgress" defaultMessage="Në progres" />
                                                    </span>
                                                ) : v.endedReason ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: dotColors[v.endedReason] || '#64748b' }}>
                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColors[v.endedReason] || '#94a3b8' }}></span>
                                                        {reasonLabels[v.endedReason] || v.endedReason}
                                                    </span>
                                                ) : null}
                                                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: '#64748b', padding: '2px 8px', borderRadius: 6, background: isActive ? '#dbeafe' : '#f1f5f9' }}>
                                                    {isActive ? <span style={{ color: '#3b82f6' }}>{fm('installation.detail.visits.active', 'aktiv')}</span> : (v.formattedDuration || fm('installation.detail.duration.zeroMin', '0 min'))}
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 34, fontSize: 12, color: '#64748b' }}>
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                <span>{fmtDtShort(v.startedAt)}</span>
                                                <span style={{ color: '#d0dbe8' }}>→</span>
                                                <span>{isActive ? fm('installation.form.now', 'tani') : fmtDtShort(v.endedAt)}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Main: Dorezimi (wider) + Files sidebar */}
                <div className="inst-detail-main">
                    <div className="inst-detail-content">
                {deliveryGroups.map((group, idx) => {
                    const coords = parseCoords(group.measureLocation);
                    const googleMapsUrl = coords ? `https://www.google.com/maps/dir/?api=1&destination=${coords[0]},${coords[1]}` : null;
                    const products = group.warehouseRecord?.products || [];

                    return (
                        <div key={idx}>
                            <div className="t-card-header">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                                <FormattedMessage id="installation.form.delivery" defaultMessage="Dorëzimi" />
                                <span className="t-card-header-badge">{fm('installation.card.productsCount', '{count} produkte', { count: products.length })}</span>
                            </div>
                            <div className="t-card-body">
                                <div className="t-delivery-info">
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></div>
                                        <div className="t-delivery-value">{group.client?.firstName || group.client?.name || '-'} {group.client?.lastName || ''}</div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="installation.form.phone" defaultMessage="Telefoni" /></div>
                                        <div className="t-delivery-value">{group.client?.phone || <span className="t-delivery-value-empty">—</span>}</div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="installation.form.location" defaultMessage="Lokacioni" /></div>
                                        <div className="t-delivery-value">
                                            {googleMapsUrl ? (
                                                <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="t-maps-link">
                                                    <svg width="11" height="14" viewBox="3 1 18 21" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>{fm('installation.form.googleMaps', 'Google Maps')}
                                                </a>
                                            ) : <span className="t-delivery-value-empty"><FormattedMessage id="installation.form.noLocation" defaultMessage="Pa lokacion" /></span>}
                                        </div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="installation.col.city" defaultMessage="Qyteti" /></div>
                                        <div className="t-delivery-value">{group.client?.city || <span className="t-delivery-value-empty">—</span>}</div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="installation.col.country" defaultMessage="Shteti" /></div>
                                        <div className="t-delivery-value">{group.client?.country || <span className="t-delivery-value-empty">—</span>}</div>
                                    </div>
                                    <div className="t-delivery-field">
                                        <div className="t-delivery-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></div>
                                        <div className="t-delivery-value">{group.project?.name || <span className="t-delivery-value-empty">—</span>}</div>
                                    </div>
                                </div>
                                <div className="t-products-section">
                                    <div className="t-wr-header">
                                        <span className="t-wr-label">{group.warehouseRecord?.label || '-'}</span>
                                        <span className="t-wr-badge">{fm('installation.card.productsCount', '{count} produkte', { count: products.length })}</span>
                                    </div>
                                    {products.length > 0 ? products.map((p: any, pIdx: number) => (
                                        <div key={pIdx} className="t-product-row">
                                            <span className="t-product-name">{p.productName || fm('installation.form.unnamed', 'Pa emër')}</span>
                                            {p.type && <span className="t-product-type">{p.type}</span>}
                                            <span className="t-product-qty">{p.quantity ? `${p.quantity} ${p.unit || ''}`.trim() : '-'}</span>
                                        </div>
                                    )) : <div className="t-product-empty"><FormattedMessage id="installation.form.noProducts" defaultMessage="Pa produkte" /></div>}
                                </div>
                            </div>
                        </div>
                    );
                })}
                    </div>{/* end inst-detail-content */}

                    {/* Sidebar: Files */}
                    <div className="inst-detail-sidebar">
                        <div>
                            <div className="t-card-header">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                <FormattedMessage id="installation.detail.documents" defaultMessage="Dokumentet" />
                            </div>
                            <div className="t-card-body">
                                <MikaFileManager
                            files={(inst.files || []).map(function(f: any) {
                                var url = f.installationFileWithUrl || f.installerSignatureWithUrl || f.clientSignatureWithUrl || '';
                                var name = f.installationFileName || f.installerSignatureFileName || f.clientSignatureFileName || 'file';
                                return { id: f.id, name: name, url: url };
                            })}
                            toolbarUpload={inst.status !== 4 ? {
                                accept: 'image/*,.pdf',
                                multiple: true,
                                onUpload: async function(files: File[]) {
                                    var formData = new FormData();
                                    for (var i = 0; i < files.length; i++) {
                                        formData.append('installation_files[]', files[i]);
                                    }
                                    await props.actions.uploadInstallationFiles(installationId, formData);
                                    toast.success(fm('installation.detail.toast.filesUploaded', 'Dokumentet u ngarkuan'));
                                    props.actions.fetchInstallation(installationId);
                                }
                            } : undefined}
                            onDelete={function(fileId: number) {
                                setDeleteConfirm({ fileIds: [fileId], message: fm('installation.detail.file.confirmOne', 'Jeni të sigurt që doni të fshini këtë dokument?') });
                            }}
                            onBulkDelete={function(fileIds: number[]) {
                                setDeleteConfirm({ fileIds: fileIds, message: fm('installation.detail.file.confirmBulk', 'Jeni të sigurt që doni të fshini {count} dokumente?', { count: fileIds.length }) });
                            }}
                                />
                            </div>
                        </div>
                    </div>{/* end inst-detail-sidebar */}
                </div>{/* end inst-detail-main */}

                {/* Bottom row: Comments + Timeline */}
                <div className="inst-detail-bottom">
                    {/* Comments */}
                    <div>
                        <div className="t-card-header" style={{ justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                <FormattedMessage id="installation.form.comments" defaultMessage="Komentet" />
                                {inst.comments?.length > 0 && <span style={{ opacity: 0.7, fontWeight: 400 }}>({inst.comments.length})</span>}
                            </div>
                        </div>
                        <div className="t-card-body">
                            {(inst.comments || []).map((c: any) => (
                                <div key={c.id} style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 6, marginBottom: 6, borderLeft: '3px solid #2a5a8a' }}>
                                    <div style={{ fontSize: 13, color: '#0f172a' }}><MentionText text={c.text} /></div>
                                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{c.user?.name || fm('installation.detail.unknownUser', 'I panjohur')} — {fmtDtShort(c.createdAt)}</div>
                                </div>
                            ))}
                            <div style={{ display: 'flex', gap: 8, marginTop: (inst.comments || []).length > 0 ? 6 : 0 }}>
                                <MentionInput
                                    multiline
                                    placeholder={fm('installation.form.commentPlaceholder', 'Shkruaj një koment...')}
                                    value={newCommentText}
                                    onChange={(next) => setNewCommentText(next)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                                    style={{ flex: 1, padding: '6px 10px', borderRadius: 4, border: '1px solid #d0dbe8', fontSize: 12, outline: 'none', minHeight: 36, resize: 'vertical' }}
                                />
                                <button onClick={handleAddComment} style={{ padding: '6px 12px', borderRadius: 4, border: 'none', alignSelf: 'flex-end', background: '#1e3a5f', color: '#fff', fontSize: 11, cursor: 'pointer' }}><FormattedMessage id="installation.form.addComment" defaultMessage="Shto" /></button>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div>
                        <div className="t-card-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            <FormattedMessage id="installation.detail.timeline.header" defaultMessage="Aktiviteti" />
                            {inst.events?.length > 0 && <span style={{ opacity: 0.7, fontWeight: 400 }}>({inst.events.length})</span>}
                        </div>
                        <div className="t-card-body" style={{ maxHeight: 260, overflow: 'auto' }}>
                            {(!inst.events || inst.events.length === 0) && <div style={{ textAlign: 'center', padding: '10px 0', color: '#94a3b8', fontSize: 11 }}>{fm('installation.detail.timeline.empty', 'Nuk ka aktivitet')}</div>}
                            {(inst.events || []).map((entry: any, idx: number) => {
                                const color = eventColors[entry.eventType] || '#94a3b8';
                                return (
                                    <div key={entry.id} style={{ display: 'flex', gap: 10, paddingBottom: idx < inst.events.length - 1 ? 10 : 0, position: 'relative' }}>
                                        {idx < inst.events.length - 1 && <div style={{ position: 'absolute', left: 5, top: 14, bottom: 0, width: 1, background: '#e2e8f0' }} />}
                                        <div style={{ width: 12, height: 12, borderRadius: '50%', flexShrink: 0, background: color, marginTop: 2, position: 'relative', zIndex: 1, boxShadow: `0 0 0 3px ${color}20` }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, color: '#0f172a' }}>{formatEvent(entry)}</div>
                                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
                                                {entry.user?.name || fm('installation.detail.timeline.system', 'Sistemi')}
                                                {entry.createdAt && <span style={{ marginLeft: 6 }}>{fmtDtShort(entry.createdAt)}</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    currentInstallation: state.installation.currentInstallation,
    permissions: state.app.permissions,
    loading: state.app.loading,
});

const mapDispatchToProps = (dispatch: any) => ({
    actions: bindActionCreators(installationActions as any, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InstallationDetail) as any) as any;
